import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useMediaQuery, Stack } from '@ltvco/refresh-lib/theme';
import { motion } from 'framer-motion';
import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import {
  BrandLogoChat,
  ChatPageContainer,
  ChatPageSidebarContainer,
  ChatPageMessagesContainer,
  ChatPageSidebar,
  ChatPageSidebarMobile,
  ChatMenuStack,
  MenuIcon,
  ReportColumn,
  FiltersColumn,
  SourcesColumn,
  CourtCaseColumn,
} from 'components/ChatBV/components';
import { CHATBV_GA4_EVENTS } from 'components/ChatBV/constants';
import { ChatContext, ChatLayoutContext } from 'components/ChatBV/context';
import { sanitizeSearchParams } from 'components/ChatBV/utils/sanitizeSearchParams';

import type { AdvancedSearchURLParams } from '@ltvco/refresh-lib/v2';
import type { OpenPersonReportInChatBVParams } from 'components/ChatBV/types';

interface ChatLayoutProps {
  onLogout: () => void;
}

export const ChatLayout = ({ onLogout }: ChatLayoutProps) => {
  const navigate = useNavigate();
  const { reportState } = useContext(ChatLayoutContext);
  const { sendMessage, handleUpgradePlanClick } = useContext(ChatContext);
  const { trackEventGA4 } = useContext(AppConfig);

  const {
    openReport,
    openFilters,
    openSources,
    openCourtCase,
    currentSources,
    closeAllPanels,
    permalink,
    reportData,
    type,
    searchParams,
    setSearchParams,
    collectionType,
    setCollectionType,
    handleCloseReport,
    handleChatTeaserClick,
    handleCloseFilters,
    handleCloseSources,
    handleCloseCourtCase,
    handleRelatedReportClick,
  } = reportState;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const normalizedSearchParams = useMemo(
    () => sanitizeSearchParams(searchParams),
    [searchParams]
  );

  const setSearchParamsSanitized = useCallback(
    (
      arg:
        | AdvancedSearchURLParams
        | ((prev: AdvancedSearchURLParams) => AdvancedSearchURLParams)
    ) => {
      if (typeof arg === 'function') {
        setSearchParams((prev) => sanitizeSearchParams(arg(prev)));
      } else {
        setSearchParams(sanitizeSearchParams(arg));
      }
    },
    [setSearchParams]
  );

  // Use MUI's useMediaQuery with custom breakpoint to target exactly 1024px
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const isColumnOpen =
    openReport || openFilters || openSources || openCourtCase;
  const currentWidth = '500px';

  const handleOpenReportInChatBV = (message: string) => {
    sendMessage(message);
  };

  const handleOpenPersonReportInChatBV = useCallback(
    (params: OpenPersonReportInChatBVParams) => {
      if (params.bvid) {
        handleChatTeaserClick()(params.bvid);
        return;
      }
      if (params.searchContext) {
        trackEventGA4({
          event_name: CHATBV_GA4_EVENTS.name,
          type: CHATBV_GA4_EVENTS.chat_teaser.type,
          subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.person_card_click,
        });
        const { firstName, middleName, lastName, city, state } =
          params.searchContext;
        const messageParts = [
          firstName,
          middleName,
          lastName,
          city,
          state,
        ].filter(Boolean);
        const searchMessage =
          messageParts.length > 0
            ? `Tell me about ${messageParts.join(' ')}`
            : 'Tell me about this person';
        sendMessage(searchMessage);
      }
    },
    [handleChatTeaserClick, sendMessage, trackEventGA4]
  );

  useEffect(() => {
    document.body.classList.add('chat-page');

    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  const headerLogoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  const handleMenuClick = () => {
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    } else {
      setIsSidebarOpen(true);
    }
  };

  const handleRecentReportsClick = () => {
    navigate('/chatbv/chat/library');
    // Close mobile sidebar when opening recent reports
    closeAllPanels();
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  };

  const handleCloseRecentReports = () => {
    navigate('/chatbv/chat');
    closeAllPanels();
  };

  const handleAccountClick = () => {
    navigate('/chatbv/account');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.account_page,
    });
  };

  return (
    <ChatPageContainer>
      {!isMobileBreakpoint ? (
        <ChatPageSidebarContainer>
          <ChatPageSidebar
            onRecentReportsClick={handleRecentReportsClick}
            onCloseRecentReports={handleCloseRecentReports}
            onUpgradePlanClick={handleUpgradePlanClick}
            onAccountClick={handleAccountClick}
            onLogout={onLogout}
          />
        </ChatPageSidebarContainer>
      ) : (
        <>
          <ChatMenuStack>
            <motion.div
              variants={headerLogoVariants}
              initial="hidden"
              animate={'visible'}
            >
              <BrandLogoChat
                width="112px"
                height="18px"
                sx={{
                  transform: 'translateY(0) translateX(0) scale(0.7)',
                }}
              />
            </motion.div>
            <MenuIcon onClick={handleMenuClick} />
          </ChatMenuStack>
          {(isSidebarOpen || isAnimatingOut) && (
            <ChatPageSidebarMobile
              isOpen={isSidebarOpen && !isAnimatingOut}
              setIsOpen={setIsSidebarOpen}
              onRecentReportsClick={handleRecentReportsClick}
              onCloseRecentReports={handleCloseRecentReports}
              onAccountClick={handleAccountClick}
              onLogout={onLogout}
            />
          )}
        </>
      )}
      <Stack
        direction={isColumnOpen ? 'row' : 'column'}
        width="100%"
        height="100%"
        spacing={0}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          ...(isColumnOpen &&
            !isMobileBreakpoint && {
              gap: '40px',
            }),
        }}
      >
        <ChatPageMessagesContainer
          sx={{
            width: isColumnOpen && !isMobileBreakpoint ? '60%' : '100%',
            height: isColumnOpen ? '98%' : '100%',
            minWidth: 0,
            ...(isColumnOpen &&
              isMobileBreakpoint && {
                width: 0,
                flexShrink: 1,
                overflow: 'hidden',
              }),
          }}
        >
          <Outlet />
        </ChatPageMessagesContainer>
        {openReport && (
          <ReportColumn
            reportData={reportData}
            onClose={handleCloseReport}
            permalink={permalink}
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            handleOpenReportInChatBV={handleOpenReportInChatBV}
            handleOpenPersonReportInChatBV={handleOpenPersonReportInChatBV}
            handleRelatedReportClick={handleRelatedReportClick}
            type={type}
          />
        )}
        {openFilters && (
          <FiltersColumn
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            isMobileBreakpoint={isMobileBreakpoint}
            onClose={handleCloseFilters}
            searchParams={normalizedSearchParams}
            setSearchParams={setSearchParamsSanitized}
            collectionType={collectionType}
            setCollectionType={setCollectionType}
          />
        )}
        {openSources && (
          <SourcesColumn
            sources={currentSources}
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            onClose={handleCloseSources}
          />
        )}
        {openCourtCase && (
          <CourtCaseColumn
            currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
            isMobileBreakpoint={isMobileBreakpoint}
            onClose={handleCloseCourtCase}
          />
        )}
      </Stack>
    </ChatPageContainer>
  );
};
